<template>
  <div class="credit-balance" v-if="isAuthenticated">
    <div class="balance-display" :class="{ 'low-balance': isLowBalance }">
      <span class="balance-icon">💰</span>
      <span class="balance-amount">{{ currentBalance }}</span>
      <span class="balance-label">{{ $t('credits.credits') }}</span>
      <span v-if="isLowBalance" class="low-balance-warning" :title="$t('credits.lowBalanceWarning')">
        ⚠️
      </span>
    </div>
    <button 
      v-if="showHistoryButton" 
      class="history-button" 
      @click="showHistory"
      :title="$t('credits.viewCreditHistory')"
    >
      {{ $t('credits.history') }}
    </button>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { useCreditStore } from '../stores/credits';
import { useAuthStore } from '../stores/auth';

const props = defineProps({
  showHistoryButton: {
    type: Boolean,
    default: true
  },
  autoRefresh: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(['showHistory']);

const creditStore = useCreditStore();
const authStore = useAuthStore();

const isAuthenticated = computed(() => authStore.isAuthenticated);
const currentBalance = computed(() => creditStore.currentBalance);
const isLowBalance = computed(() => creditStore.isLowBalance);

const showHistory = () => {
  emit('showHistory');
};

onMounted(async () => {
  if (props.autoRefresh && isAuthenticated.value) {
    try {
      await creditStore.fetchBalance();
    } catch (error) {
      console.error('Failed to fetch credit balance:', error);
    }
  }
});
</script>

<style scoped>
.credit-balance {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.8rem;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  font-size: 0.85rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  margin-right: 0.5rem;
}

.balance-display {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-weight: 600;
}

.balance-icon {
  font-size: 1.2rem;
}

.balance-amount {
  color: #4CAF50;
  font-size: 1.1rem;
}

.balance-label {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
  font-weight: normal;
}

.low-balance {
  color: #ff9800;
}

.low-balance .balance-amount {
  color: #ff9800;
}

.low-balance-warning {
  margin-left: 0.25rem;
  font-size: 0.9rem;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.history-button {
  padding: 0.25rem 0.75rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  color: white;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.2s;
}

.history-button:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
}

/* Dark theme adjustments */
@media (prefers-color-scheme: dark) {
  .credit-balance {
    background: rgba(0, 0, 0, 0.2);
  }
  
  .history-button {
    background: rgba(0, 0, 0, 0.2);
    border-color: rgba(255, 255, 255, 0.1);
  }
  
  .history-button:hover {
    background: rgba(0, 0, 0, 0.3);
  }
}
</style>
